import { useEffect, useRef, useState } from "react";
import { Loader2, Save, Check, X, Upload, FileText, FileDown } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail, API } from "@/lib/api";

const FIELDS = [
  { key: "presenting_issues", label: "Presenting issues", rows: 2 },
  { key: "approach", label: "Approach", rows: 2 },
  { key: "observations", label: "Observations of the client during the session", rows: 3, required: true },
  { key: "client_strengths", label: "Client's strengths", rows: 2 },
  { key: "plan_adjustments", label: "Adjustments to the original counselling plan", rows: 2, placeholder: "N/A" },
  { key: "decisions", label: "Decisions made by the client during the session", rows: 2, required: true },
  { key: "followup_client", label: "Follow-up for the client", rows: 2, placeholder: "Note any homework or additional resources given. N/A if none.", required: true },
  { key: "followup_counsellor", label: "Follow-up for the counsellor", rows: 2, placeholder: "N/A" },
  { key: "referrals", label: "Referrals (as applicable)", rows: 2, placeholder: "N/A" },
  { key: "referral_followup_date", label: "Agreed date to follow up client regarding referral service", type: "date", placeholder: "N/A" },
  { key: "supervision_reflection", label: "Reflection on supervision needs", rows: 2 },
];

/**
 * Inline editor for session notes. Mounts as part of the BookingDetailModal once
 * the admin clicks "Mark complete". Autosaves silently every ~1.5s.
 */
export default function SessionNotesEditor({ booking, onAttached, onCompleted }) {
  const [notes, setNotes] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingState, setSavingState] = useState("idle"); // 'idle' | 'saving' | 'saved'
  const [completing, setCompleting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const saveTimer = useRef(null);

  useEffect(() => {
    let cancelled = false;
    api.get(`/admin/bookings/${booking.id}/notes`)
      .then((r) => { if (!cancelled) setNotes(r.data); })
      .catch((err) => toast.error(formatApiErrorDetail(err.response?.data?.detail)))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [booking.id]);

  // Debounced autosave
  useEffect(() => {
    if (!notes || loading) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      setSavingState("saving");
      try {
        const payload = Object.fromEntries(
          ["session_number", "presenting_issues", "approach", "observations", "client_strengths", "plan_adjustments", "decisions", "followup_client", "followup_counsellor", "referrals", "referral_followup_date", "supervision_reflection"].map((k) => [k, notes[k] ?? ""])
        );
        payload.session_number = Number(payload.session_number) || 1;
        await api.put(`/admin/bookings/${booking.id}/notes`, payload);
        setSavingState("saved");
        setTimeout(() => setSavingState("idle"), 1500);
      } catch {
        setSavingState("idle");
      }
    }, 1200);
    return () => saveTimer.current && clearTimeout(saveTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notes, loading]);

  const update = (k, v) => setNotes((x) => ({ ...x, [k]: v }));

  const completeNotes = async () => {
    setCompleting(true);
    try {
      // Flush any pending autosave first
      if (saveTimer.current) {
        clearTimeout(saveTimer.current);
        const payload = Object.fromEntries(
          ["session_number", "presenting_issues", "approach", "observations", "client_strengths", "plan_adjustments", "decisions", "followup_client", "followup_counsellor", "referrals", "referral_followup_date", "supervision_reflection"].map((k) => [k, notes[k] ?? ""])
        );
        payload.session_number = Number(payload.session_number) || 1;
        await api.put(`/admin/bookings/${booking.id}/notes`, payload);
      }
      await api.post(`/admin/bookings/${booking.id}/notes/complete`);
      await api.patch(`/admin/bookings/${booking.id}`, { status: "completed" });
      toast.success("Session notes saved & booking marked complete");
      onCompleted();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setCompleting(false);
    }
  };

  const uploadAttachment = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/admin/bookings/${booking.id}/notes/attachment`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setNotes((x) => ({ ...x, attachment: r.data.attachment }));
      toast.success("File attached to notes");
      onAttached && onAttached();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  if (loading || !notes) {
    return (
      <div className="bg-cream rounded-2xl border border-sand/60 p-6 mt-4 flex items-center justify-center text-ink-soft">
        <Loader2 className="animate-spin mr-2" size={16} /> Loading notes…
      </div>
    );
  }

  const isComplete = notes.status === "complete";
  const token = localStorage.getItem("ams_token");
  const fileUrl = notes.attachment
    ? `${API}/admin/bookings/${booking.id}/notes/attachment?auth=${encodeURIComponent(token || "")}`
    : null;
  const labelCls = "block text-[11px] uppercase tracking-[0.18em] text-ink-soft mb-1.5";
  const inputCls = "w-full bg-cream border border-sand/80 rounded-xl px-3 py-2.5 text-ink focus:outline-none focus:border-navy focus:ring-1 focus:ring-navy text-sm font-sans resize-y";

  return (
    <div className="bg-cream rounded-2xl border border-sand/60 p-5 mt-4" data-testid="session-notes-editor">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <FileText size={16} className="text-navy" />
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-ink-soft">Session notes</div>
            <div className="font-serif text-lg text-ink">Session #{notes.session_number} · {notes.session_date}</div>
          </div>
        </div>
        <div className="flex items-center gap-2 text-xs">
          {isComplete ? (
            <span className="inline-flex items-center gap-1.5 text-navy bg-navy/10 px-3 py-1 rounded-full uppercase tracking-[0.18em] text-[10px]">
              <Check size={11} /> Complete
            </span>
          ) : (
            <span className="text-ink-soft">
              {savingState === "saving" ? "Saving…" : savingState === "saved" ? "Saved" : "Draft — autosaves as you type"}
            </span>
          )}
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-3 mb-3">
        <div>
          <label className={labelCls}>Session #</label>
          <input
            type="number"
            min="1"
            max="500"
            className={`${inputCls} resize-none`}
            value={notes.session_number}
            onChange={(e) => update("session_number", e.target.value)}
            disabled={isComplete}
            data-testid="notes-session-number"
          />
        </div>
        <div className="sm:col-span-2">
          <label className={labelCls}>Counsellor</label>
          <input className={`${inputCls} resize-none opacity-70 cursor-not-allowed`} value="Jesse Winter" disabled />
        </div>
      </div>

      <div className="space-y-3">
        {FIELDS.map((f) => (
          <div key={f.key}>
            <label className={labelCls}>
              {f.label}{f.required && <span className="text-red-700 ml-1">*</span>}
            </label>
            {f.type === "date" ? (
              <input
                type="date"
                className={`${inputCls} resize-none`}
                value={notes[f.key] || ""}
                onChange={(e) => update(f.key, e.target.value)}
                disabled={isComplete}
                data-testid={`notes-${f.key}`}
              />
            ) : (
              <textarea
                rows={f.rows || 2}
                className={inputCls}
                placeholder={f.placeholder}
                value={notes[f.key] || ""}
                onChange={(e) => update(f.key, e.target.value)}
                disabled={isComplete}
                data-testid={`notes-${f.key}`}
              />
            )}
          </div>
        ))}
      </div>

      <div className="mt-5 pt-4 border-t border-sand/40">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            {notes.attachment ? (
              <a href={fileUrl} target="_blank" rel="noopener noreferrer" data-testid="notes-attachment-link" className="inline-flex items-center gap-2 text-sm text-navy hover:underline">
                <FileDown size={14} /> {notes.attachment.original_filename}
              </a>
            ) : (
              <>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,application/pdf,image/*"
                  onChange={uploadAttachment}
                  className="hidden"
                  data-testid="notes-attachment-input"
                />
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading || isComplete}
                  className="inline-flex items-center gap-1.5 rounded-full bg-cream border border-sand/80 hover:border-navy/40 px-3 py-1.5 text-xs text-ink-soft transition-colors disabled:opacity-50"
                >
                  {uploading ? <Loader2 className="animate-spin" size={12} /> : <Upload size={12} />} Attach file (PDF / image)
                </button>
              </>
            )}
          </div>
          {!isComplete && (
            <button
              type="button"
              onClick={completeNotes}
              disabled={completing}
              data-testid="notes-complete-btn"
              className="inline-flex items-center gap-2 rounded-full bg-navy text-cream px-5 py-2 text-xs font-medium hover:bg-navy-deep transition-colors disabled:opacity-50"
            >
              {completing ? <Loader2 className="animate-spin" size={14} /> : <Check size={14} />}
              Save & mark complete
            </button>
          )}
          <a
            href={`${API}/admin/bookings/${booking.id}/notes/pdf?auth=${encodeURIComponent(token || "")}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="notes-pdf-link"
            className="inline-flex items-center gap-1.5 rounded-full bg-cream border border-sand/80 hover:border-navy/40 px-3 py-1.5 text-xs text-ink-soft transition-colors"
          >
            <FileDown size={12} /> Download PDF
          </a>
        </div>
        {!isComplete && (
          <p className="text-[11px] text-ink-soft mt-2 italic">
            Observations, Decisions and Follow-up are required before this session can be marked complete.
          </p>
        )}
      </div>
    </div>
  );
}
